import { Users, Boxes, Tag, Trophy, Network, CreditCard, ClipboardList, Building2, UserPlus, Gift } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { COMPANY_VIEWERS, SUBSCRIPTION_MANAGERS, TEAM_ROLES } from '@/lib/permissions'

/**
 * The tabs of Membros, in David's order (2026-10-06): Membros, Assinaturas,
 * Grupos, Tags, Organograma, Gamificação; Empresas (F-93) left its own page in
 * Administração for a tab here on 2026-10-16 (`/companies` is redirected by `routes.js`). Gestão de assinaturas moved
 * here from Monetização; Logs left for Administração › Logs on 2026-10-14
 * (`?tab=activity-logs` is redirected by `routes.js`). Each tab keeps the roles it had before, so the page
 * opens to staff and to finance, and each person sees only their tabs:
 * finance lands on Assinaturas and sees nothing else.
 *
 * The key is the `?tab=` value and stays in English.
 */
export type MembersTab =
  | 'members'
  | 'pending'
  | 'subscriptions'
  | 'companies'
  | 'groups'
  | 'tags'
  | 'custom-fields'
  | 'org-chart'
  | 'gamification'
  | 'referrals'

const STAFF = TEAM_ROLES
const ADMINS = ['owner', 'admin']

export interface MembersTabDef {
  key: MembersTab
  labelKey: string
  icon: LucideIcon
  roles: string[]
}

export const MEMBERS_TABS: MembersTabDef[] = [
  { key: 'members', labelKey: 'members:admin.members', icon: Users, roles: STAFF },
  /* F-150: the requests to join, owner and admin only (the API answers 403 to anyone else).
     Shown while the community approves signups, or when a link asks for it (`/_/members/pending`). */
  { key: 'pending', labelKey: 'members:admin.pending', icon: UserPlus, roles: ADMINS },
  /* F-45: owner, admin and finance act on subscriptions; a moderator never
     reached them in Monetização and does not here. */
  { key: 'subscriptions', labelKey: 'subscriptionAdmin:tab', icon: CreditCard, roles: SUBSCRIPTION_MANAGERS },
  /* F-93 1A: the companies; owner and admin manage, finance and the moderator read
     (the moderator without the contract, which the API leaves out). `&company={id}` opens one. */
  { key: 'companies', labelKey: 'companies:page.title', icon: Building2, roles: COMPANY_VIEWERS },
  { key: 'groups', labelKey: 'members:admin.groups', icon: Boxes, roles: STAFF },
  { key: 'tags', labelKey: 'members:admin.tags', icon: Tag, roles: STAFF },
  /* Formulário (F-85): the member's sign-up form. The team reads; only owner
     and admin write custom fields (the tab says so, the API answers 403). */
  { key: 'custom-fields', labelKey: 'members:admin.customFields', icon: ClipboardList, roles: STAFF },
  { key: 'org-chart', labelKey: 'members:admin.orgChart', icon: Network, roles: STAFF },
  /* PUT /gamification/config is owner/admin. */
  { key: 'gamification', labelKey: 'members:admin.gamification', icon: Trophy, roles: ADMINS },
  /* F-159: who brought people in. Owner, admin and moderator, as `GET /api/referrals/report` (finance: 403). */
  { key: 'referrals', labelKey: 'referrals:report.tab', icon: Gift, roles: ['owner', 'admin', 'moderator'] },
]

/** Everyone who opens Membros: the union of the tabs' roles. */
export const MEMBERS_AREA_ROLES = Array.from(new Set(MEMBERS_TABS.flatMap((tab) => tab.roles)))

/** The tab a URL asks for (`/members?tab=…`), or null. */
export const tabFromUrl = (): MembersTab | null => {
  const asked = new URLSearchParams(window.location.search).get('tab')
  return MEMBERS_TABS.some((tab) => tab.key === asked) ? (asked as MembersTab) : null
}
